import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { IconGithub, IconPlus, IconShield } from '../components/Icon';
import { signInWithGoogle, useSession } from '../lib/session';
import { useOnline } from '../lib/online';

/**
 * Artboard 1a — the QR destination.
 * One screen, one tap. It depends on nothing but the app shell, because it is
 * the first thing two hundred people load simultaneously on hotel wifi with
 * their luggage still in hand.
 */
export default function Login() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session) return <Navigate to={session.needsWelcome ? '/welcome' : (location.state?.from ?? '/now')} replace />;


  async function google() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      navigate('/auth/callback', { replace: true, state: location.state });
    } catch {
      setError(
        online
          ? "Google didn't finish signing you in. Try again, or use your email address instead."
          : 'There is no connection right now. The hotel wifi drops for a few seconds at a time — try again in a moment.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="auth auth-split">
      <div className="auth-hero">
        {/* The one image on the critical path — the first thing two hundred
            people load at once. WebP with a JPEG fallback, and no size larger
            than the box can show: the hero caps at 640px tall, so 1100w covers
            the desktop split at 2x and 700w covers a phone.
            `fetchPriority="high"` because this is the LCP element; everything
            else on this screen is text. */}
        <picture>
          {/* Art-directed, not one image scaled: the phone box is landscape
              (~362x320) and the desktop box is portrait (~525x640). Serving the
              portrait to a phone ships three quarters of its height to be
              cropped away unseen by `object-fit: cover`. */}
          <source media="(min-width: 900px)" type="image/avif" srcSet="/img/landing-lg.avif" />
          <source media="(min-width: 900px)" type="image/webp" srcSet="/img/landing-lg.webp" />
          <source media="(min-width: 900px)" srcSet="/img/landing-lg.jpg" />
          <source type="image/avif" srcSet="/img/landing-sm.avif" />
          <source type="image/webp" srcSet="/img/landing-sm.webp" />
          <img
            src="/img/landing-sm.jpg"
            width={800}
            height={708}
            fetchPriority="high"
            decoding="async"
            alt="Meg and Videet on a Brooklyn street, laughing"
          />
        </picture>
      </div>

      <div className="auth-body">
        <div className="kicker">Khao Lak · 5–8 November 2026</div>
        <h1 className="auth-title">Meg &amp; Videet</h1>
        <p className="auth-lede">
          The schedule, the resort map and everyone's photos, all week. Sign in so your uploads carry your name.
        </p>
      </div>

      <div className="auth-foot">
        {error && (
          <div className="card" style={{ background: 'var(--color-accent-200)', padding: '12px 14px' }} role="alert">
            <p style={{ margin: 0, fontSize: 13, color: 'var(--color-accent-900)' }}>{error}</p>
          </div>
        )}

        <div className="card reassure">
          <IconShield size={17} className="reassure-icon" />
          <p>We built this ourselves, sometimes at unreasonable hours. Nobody but us gets your photos or your email.</p>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block"
          style={{ minHeight: 54, fontSize: 16, gap: 10, margin: 0 }}
          onClick={google}
          disabled={busy}
        >
          <IconPlus size={18} />
          {busy ? 'Signing you in…' : 'Continue with Google'}
        </button>

        {/* Back to our own screen. `/auth/start` makes the user exist first,
            which is the only thing managed login was doing for us — and it did
            it on a page that offered a second "Continue with Google" next to
            the one the guest just walked past. */}
        <Link className="btn btn-ghost" style={{ minHeight: 44, fontSize: 14, alignSelf: 'center' }} to="/login/email">
          Use my email address
        </Link>

        <a className="src-link" href="https://github.com/megandvideet/site" rel="noreferrer noopener" target="_blank">
          <IconGithub /> Source on GitHub
        </a>
      </div>
    </div>
  );
}
